import { useQuery, UseQueryOptions } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { DocumentResult } from '@utils/types/editors/generic.ts';

type Props = {
  sessionId?: string;
  documentId?: string;
  enabled?: boolean;
  token: string;
};

async function getSharedDocument({
  sessionId,
  documentId,
  token
}: Props): Promise<DocumentResult> {
  const url = GET_URL(sessionId, documentId).sharedDocument;

  // ✅ COST OPTIMIZATION: Try direct download from Azure Blob Storage first
  // This saves bandwidth costs by bypassing the load balancer for shared editor loads
  try {
    const directResponse = await Axios.get<DocumentResult>(url, {
      params: {
        token,
        direct_download: true
      }
    });

    // NEW UNIFIED API: Check if backend returned a download_url
    if (directResponse.data.result?.download_url) {
      // Direct download mode: fetch document from Azure Blob Storage
      const blobResponse = await fetch(directResponse.data.result.download_url);
      if (!blobResponse.ok) {
        throw new Error('Failed to fetch from Azure Blob Storage');
      }

      // The blob contains the raw document (DocumentSubtitleV1 or DocumentTranscriptV1)
      const documentContent = await blobResponse.json();

      // Backwards compatibility for Subtitle V0: legacy/unedited caption blobs are
      // stored as a bare JSON array of segments. The non-direct backend path wraps
      // these in a V1 object (see api-scriptix document.py load_document); the direct
      // blob fetch bypasses that, so we must wrap here too — otherwise the editor sees
      // an array without a `.document` field and renders "No segments available".
      const normalizedDocument = Array.isArray(documentContent)
        ? {
            document_type: 'caption',
            document: documentContent,
            version: 'v1'
          }
        : documentContent;

      // Return the API response with the document field populated from blob
      return {
        count: directResponse.data.count,
        total_results: directResponse.data.total_results,
        result: {
          ...directResponse.data.result,
          document: normalizedDocument,
          download_url: null,
          expires_in: null
        }
      } as DocumentResult;
    }

    // Embedded document mode: document field is already populated
    // This happens when direct_download=false or backend doesn't support it
    return directResponse.data;
  } catch (directError) {
    // Fallback: Request without direct_download parameter
    const { data } = await Axios.get(url, {
      params: { token }
    });
    return data;
  }
}

export function useGetSharedDocument(
  props: Props & { token?: string },
  options?: UseQueryOptions<DocumentResult, AxiosError<BaseError>>
) {
  return useQuery<DocumentResult, AxiosError<BaseError>>(
    ['sharedDocument', props.sessionId, props.documentId, props.token],
    () => getSharedDocument(props),
    {
      ...(options ?? {}),
      enabled:
        props.enabled ||
        (!!props.sessionId && !!props.documentId && !!props.token),
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false
    }
  );
}
